import { useState, useEffect } from 'react';
import { useNumberInput } from '../../hooks';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Button,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Snackbar,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as SettledIcon,
  ArrowUpward as CollectIcon,
  ArrowDownward as PayOutIcon,
  Refresh as RecalculateIcon,
} from '@mui/icons-material';
import { useAccountingDetail } from '../../hooks';
import { useDocumentTitle } from '../../hooks/core';
import { ledgerApi, accountingApi } from '../../api';
import { PlayerAvatar } from '../../components/shared/PlayerAvatar';
import type { BoardAccountingDetail, LedgerEntry } from '../../types';
import { formatEntryType, formatAccountingDate } from '../../utils/accounting';

interface AccountingDetailPageProps {
  type: 'user' | 'offline';
}

export const AccountingDetailPage = ({ type }: AccountingDetailPageProps) => {
  const { userId, offlinePlayerId } = useParams<{ userId?: string; offlinePlayerId?: string }>();
  const id = type === 'user' ? userId : offlinePlayerId;
  const navigate = useNavigate();
  const { detail, isLoading, error, refresh } = useAccountingDetail(type, id || '');

  // Payment dialog state
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedBoard, setSelectedBoard] = useState<BoardAccountingDetail | null>(null);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentNotes, setPaymentNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Use number input hook for better typing UX (allows clearing, validates on blur)
  const paymentInput = useNumberInput(paymentAmount, setPaymentAmount, { min: 0 });

  // Settle up dialog state
  const [settleUpDialogOpen, setSettleUpDialogOpen] = useState(false);
  const [settlingUp, setSettlingUp] = useState(false);

  // Recalculate state
  const [recalculateEnabled, setRecalculateEnabled] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [recalculateSnackbar, setRecalculateSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Check if recalculate feature is enabled
  useEffect(() => {
    accountingApi.getRecalculateStatus()
      .then((status) => setRecalculateEnabled(status.enabled))
      .catch(() => setRecalculateEnabled(false));
  }, []);

  const handleRecalculate = async () => {
    if (!id) return;
    setRecalculating(true);
    try {
      await accountingApi.recalculateUserAccounting(id, type === 'offline');
      setRecalculateSnackbar({
        open: true,
        message: 'Accounting recalculation started. Please refresh in a moment to see updated values.',
        severity: 'success',
      });
      // Refresh after a short delay to allow background job to complete
      setTimeout(() => refresh(), 2000);
    } catch (err) {
      console.error(err);
      setRecalculateSnackbar({
        open: true,
        message: 'Failed to start recalculation. Please try again.',
        severity: 'error',
      });
    } finally {
      setRecalculating(false);
    }
  };

  const displayName = detail?.isOfflinePlayer
    ? `${detail.offlinePlayer?.displayName} (offline)`
    : detail?.user?.displayName || 'Unknown';

  useDocumentTitle(detail ? `Accounting - ${displayName}` : 'Accounting');

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!detail) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        User not found
      </Alert>
    );
  }

  const handleOpenPaymentDialog = (board: BoardAccountingDetail) => {
    const amountDue = board.totalOwed - board.totalPaid;
    setSelectedBoard(board);
    setPaymentAmount(amountDue > 0 ? amountDue : 0);
    setPaymentNotes('');
    setPaymentDialogOpen(true);
  };

  const handleRecordPayment = async () => {
    if (!selectedBoard || paymentAmount <= 0) return;
    setSaving(true);
    try {
      await ledgerApi.recordPayment(selectedBoard.board.id, {
        userId: detail.isOfflinePlayer ? undefined : detail.userId,
        offlinePlayerId: detail.isOfflinePlayer ? detail.offlinePlayerId : undefined,
        amount: paymentAmount,
        description: paymentNotes.trim() || 'Payment for squares',
      });
      setPaymentDialogOpen(false);
      setSelectedBoard(null);
      setPaymentNotes('');
      refresh();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleMarkWinningsPaid = async (board: BoardAccountingDetail) => {
    setSaving(true);
    try {
      await ledgerApi.markWinningsPaid(board.board.id, {
        userId: detail.isOfflinePlayer ? undefined : detail.userId,
        offlinePlayerId: detail.isOfflinePlayer ? detail.offlinePlayerId : undefined,
        paid: !board.winningsFullyPaid,
      });
      refresh();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleSettleUp = async () => {
    setSettlingUp(true);
    const epsilon = 0.005; // Half a cent for floating point comparison
    try {
      // Settle ALL boards regardless of aggregate balance direction
      // This handles the "offsetting balances" case where some boards have positive
      // and some have negative balances that cancel out in aggregate
      for (const board of detail.boards) {
        // Calculate board's net balance: negative = player owes, positive = manager owes
        const boardNetBalance = board.totalEarned + board.totalPaid - board.totalOwed - board.winningsPaidOut;

        if (boardNetBalance < -epsilon) {
          // Player owes money on this board - record payment for the full amount owed
          const amountOwedOnBoard = Math.abs(boardNetBalance);
          await ledgerApi.recordPayment(board.board.id, {
            userId: detail.isOfflinePlayer ? undefined : detail.userId,
            offlinePlayerId: detail.isOfflinePlayer ? detail.offlinePlayerId : undefined,
            amount: amountOwedOnBoard,
            description: 'Settle up payment',
          });
        } else if (boardNetBalance > epsilon) {
          // Manager owes money on this board - mark as paid out
          // This handles both unpaid winnings AND overpayments (positive balance without winnings)
          // The backend MarkWinningsPaid uses NetBalance to determine the payout amount,
          // so it will correctly handle any positive balance regardless of source
          await ledgerApi.markWinningsPaid(board.board.id, {
            userId: detail.isOfflinePlayer ? undefined : detail.userId,
            offlinePlayerId: detail.isOfflinePlayer ? detail.offlinePlayerId : undefined,
            paid: true,
          });
        }
      }

      setSettleUpDialogOpen(false);
      refresh();
    } catch (err) {
      console.error(err);
    } finally {
      setSettlingUp(false);
    }
  };

  // Use a small epsilon to handle floating point comparison (half a cent)
  const epsilon = 0.005;

  // Calculate what settle up will do - based on NET balance, not individual items
  const aggregateNetBalance = detail.netBalance;
  const netBalanceIsZero = Math.abs(aggregateNetBalance) < epsilon;

  // Build settle up details for display - show ALL boards that will be settled
  const settleUpDetails: { boardName: string; paymentToRecord: number; markWinningsPaid: boolean }[] = [];

  for (const board of detail.boards) {
    const boardNetBalance = board.totalEarned + board.totalPaid - board.totalOwed - board.winningsPaidOut;

    if (boardNetBalance < -epsilon) {
      // Player owes money on this board
      settleUpDetails.push({
        boardName: board.board.name,
        paymentToRecord: Math.abs(boardNetBalance),
        markWinningsPaid: false,
      });
    } else if (boardNetBalance > epsilon) {
      // Manager owes money on this board (unpaid winnings or overpayment)
      settleUpDetails.push({
        boardName: board.board.name,
        paymentToRecord: 0,
        markWinningsPaid: true,
      });
    }
  }

  // Check if ALL boards are settled by looking at each board's NET BALANCE
  // A board is only truly settled when its net balance is $0
  const allBoardsSettled = detail.boards.every(board => {
    const boardNetBalance = board.totalEarned + board.totalPaid - board.totalOwed - board.winningsPaidOut;
    return Math.abs(boardNetBalance) < epsilon;
  });
  const hasUnsettledBoards = !allBoardsSettled;

  // Calculate actionable amounts based on aggregate
  const amountToCollect = aggregateNetBalance < -epsilon ? Math.abs(aggregateNetBalance) : 0;
  const amountToPayOut = aggregateNetBalance > epsilon ? aggregateNetBalance : 0;

  // Show "collect" or "pay out" action card only when aggregate is non-zero
  const showCollectOrPayCard = !netBalanceIsZero;

  // When aggregate is zero but individual boards have balances, show a different state
  // This happens when some boards have positive balance (manager owes) and others negative (player owes)
  const hasOffsettingBalances = netBalanceIsZero && hasUnsettledBoards;

  // Fully settled only when ALL boards have $0 balance
  const isFullySettled = allBoardsSettled;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/accounting')}
          variant="outlined"
        >
          Back
        </Button>
        <PlayerAvatar
          displayName={detail.isOfflinePlayer ? (detail.offlinePlayer?.displayName || '') : (detail.user?.displayName || '')}
          handle={detail.isOfflinePlayer ? undefined : detail.user?.handle}
          iconName={detail.isOfflinePlayer ? undefined : detail.user?.iconName}
          isOffline={detail.isOfflinePlayer}
          size="large"
          showBadge={false}
        />
        <Typography variant="h4">
          {displayName}
        </Typography>
        {isFullySettled && (
          <Chip label="Fully Settled" color="success" icon={<SettledIcon />} />
        )}
        {recalculateEnabled && (
          <Tooltip title="Recalculate accounting from ledger entries">
            <Button
              variant="outlined"
              size="small"
              startIcon={
                <RecalculateIcon
                  sx={recalculating ? {
                    animation: 'spin 1s linear infinite',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' },
                    },
                  } : undefined}
                />
              }
              onClick={handleRecalculate}
              disabled={recalculating}
              sx={{ ml: 'auto' }}
            >
              {recalculating ? 'Recalculating...' : 'Recalculate'}
            </Button>
          </Tooltip>
        )}
      </Box>

      {/* Primary Action Card - What does the manager need to do? */}
      {showCollectOrPayCard ? (
        <Paper
          sx={{
            p: 3,
            mb: 3,
            bgcolor: amountToCollect > 0 ? 'error.50' : 'success.50',
            border: 2,
            borderColor: amountToCollect > 0 ? 'error.main' : 'success.main',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            {amountToCollect > 0 ? (
              <CollectIcon sx={{ fontSize: 40, color: 'error.main' }} />
            ) : (
              <PayOutIcon sx={{ fontSize: 40, color: 'success.main' }} />
            )}
            <Box>
              <Typography variant="overline" color="text.secondary">
                {amountToCollect > 0 ? 'You Need to Collect' : 'You Need to Pay Out'}
              </Typography>
              <Typography variant="h4" fontWeight="bold" color={amountToCollect > 0 ? 'error.main' : 'success.main'}>
                ${amountToCollect > 0 ? amountToCollect.toFixed(2) : amountToPayOut.toFixed(2)}
              </Typography>
            </Box>
            <Box sx={{ ml: 'auto' }}>
              <Button
                variant="contained"
                color={amountToCollect > 0 ? 'error' : 'success'}
                size="large"
                onClick={() => setSettleUpDialogOpen(true)}
              >
                {amountToCollect > 0 ? 'Record Payment Received' : 'Mark as Paid Out'}
              </Button>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {amountToCollect > 0
              ? `${displayName} owes you this amount for their squares across ${detail.boards.length} board${detail.boards.length > 1 ? 's' : ''}.`
              : `You owe ${displayName} this amount in winnings across ${detail.boards.length} board${detail.boards.length > 1 ? 's' : ''}.`
            }
          </Typography>
        </Paper>
      ) : hasOffsettingBalances ? (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'warning.50', border: 2, borderColor: 'warning.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{ color: 'warning.main', fontSize: 40 }}>⚖️</Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight="bold" color="warning.dark">
                Offsetting Balances
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {displayName} has boards where they owe you money and boards where you owe them winnings.
                The amounts offset each other (net $0), but individual boards need to be settled.
              </Typography>
            </Box>
            <Box>
              <Button
                variant="contained"
                color="warning"
                size="large"
                onClick={() => setSettleUpDialogOpen(true)}
              >
                Settle All Boards
              </Button>
            </Box>
          </Box>
        </Paper>
      ) : (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'success.50', border: 2, borderColor: 'success.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SettledIcon sx={{ fontSize: 40, color: 'success.main' }} />
            <Box>
              <Typography variant="h5" fontWeight="bold" color="success.main">
                All Settled Up
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No outstanding balance with {displayName}.
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Historical Summary - Collapsible */}
      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight="bold">
            Historical Summary
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="overline" color="text.secondary">Square Purchases</Typography>
              <Typography variant="h6">${detail.totalOwed.toFixed(2)}</Typography>
              <Typography variant="caption" color="text.secondary">
                Total cost of all squares claimed
              </Typography>
            </Box>
            <Box>
              <Typography variant="overline" color="text.secondary">Payments Received</Typography>
              <Typography variant="h6" color="success.main">${detail.totalPaid.toFixed(2)}</Typography>
              <Typography variant="caption" color="text.secondary">
                Money collected from this player
              </Typography>
            </Box>
            <Box>
              <Typography variant="overline" color="text.secondary">Winnings Earned</Typography>
              <Typography variant="h6" color="info.main">${detail.totalEarned.toFixed(2)}</Typography>
              <Typography variant="caption" color="text.secondary">
                Total prize money won
              </Typography>
            </Box>
            <Box>
              <Typography variant="overline" color="text.secondary">Winnings Paid Out</Typography>
              <Typography variant="h6">${detail.winningsPaidOut.toFixed(2)}</Typography>
              <Typography variant="caption" color="text.secondary">
                Prize money already paid
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" color="text.secondary">
            <strong>How the balance is calculated:</strong> Net Balance = Winnings Earned + Payments Received - Square Purchases.
            A negative balance means the player owes you money. A positive balance means you owe them winnings.
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* Per-Board Details */}
      <Typography variant="h6" gutterBottom>
        Boards ({detail.boards.length})
      </Typography>

      {detail.boards.map((board) => (
        <BoardCard
          key={board.board.id}
          board={board}
          onRecordPayment={() => handleOpenPaymentDialog(board)}
          onMarkWinningsPaid={() => handleMarkWinningsPaid(board)}
          saving={saving}
        />
      ))}

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)}>
        <DialogTitle>Record Payment</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Recording payment from <strong>{displayName}</strong> for{' '}
            <strong>{selectedBoard?.board.name}</strong>
          </Typography>
          <TextField
            fullWidth
            type="number"
            label="Amount"
            value={paymentInput.value}
            onChange={paymentInput.onChange}
            onBlur={paymentInput.onBlur}
            sx={{ mt: 2 }}
            inputProps={{ min: 0, step: 0.01 }}
          />
          <TextField
            fullWidth
            label="Notes (optional)"
            placeholder="e.g., Venmo txn #12345, Cash received"
            value={paymentNotes}
            onChange={(e) => setPaymentNotes(e.target.value)}
            sx={{ mt: 2 }}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleRecordPayment} variant="contained" disabled={saving || paymentAmount <= 0}>
            {saving ? 'Saving...' : 'Record Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Settle Up Dialog */}
      <Dialog open={settleUpDialogOpen} onClose={() => setSettleUpDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Settle Up</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Settling up with <strong>{displayName}</strong>:
          </Typography>

          {/* Summary of what's happening */}
          <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: hasOffsettingBalances ? 'warning.50' : (amountToCollect > 0 ? 'error.50' : 'success.50'), borderRadius: 1 }}>
            {hasOffsettingBalances ? (
              <Typography>
                This will settle all boards at once: record payments where <strong>{displayName}</strong> owes you,
                and mark winnings as paid where you owe them. Net result: all boards balanced to $0.
              </Typography>
            ) : amountToCollect > 0 ? (
              <Typography>
                <strong>{displayName}</strong> owes <strong>${amountToCollect.toFixed(2)}</strong>.
                Recording this payment will bring their balance to $0.
              </Typography>
            ) : (
              <Typography>
                You owe <strong>{displayName}</strong> <strong>${amountToPayOut.toFixed(2)}</strong> in winnings.
                Marking winnings as paid will bring their balance to $0.
              </Typography>
            )}
          </Box>

          {settleUpDetails.length > 0 && (
            <>
              <Typography variant="subtitle2" gutterBottom>
                Breakdown by board:
              </Typography>
              <Box>
                {settleUpDetails.map((item, idx) => (
                  <Box key={idx} sx={{ mb: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                    <Typography variant="subtitle2">{item.boardName}</Typography>
                    {item.paymentToRecord > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        - Record payment: ${item.paymentToRecord.toFixed(2)}
                      </Typography>
                    )}
                    {item.markWinningsPaid && (
                      <Typography variant="body2" color="text.secondary">
                        - Mark winnings as paid out
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettleUpDialogOpen(false)} disabled={settlingUp}>Cancel</Button>
          <Button onClick={handleSettleUp} variant="contained" disabled={settlingUp}>
            {settlingUp ? 'Settling...' : 'Confirm Settle Up'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recalculate Snackbar */}
      <Snackbar
        open={recalculateSnackbar.open}
        autoHideDuration={6000}
        onClose={() => setRecalculateSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setRecalculateSnackbar(prev => ({ ...prev, open: false }))}
          severity={recalculateSnackbar.severity}
          sx={{ width: '100%' }}
        >
          {recalculateSnackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Board Card Component
interface BoardCardProps {
  board: BoardAccountingDetail;
  onRecordPayment: () => void;
  onMarkWinningsPaid: () => void;
  saving: boolean;
}

const BoardCard = ({ board, onRecordPayment, onMarkWinningsPaid, saving }: BoardCardProps) => {
  const epsilon = 0.005; // Half a cent for floating point comparison
  const amountDue = board.totalOwed - board.totalPaid;
  const isPaid = amountDue <= epsilon;
  const hasWinnings = board.totalEarned > epsilon;

  // Board net balance: NetBalance = TotalEarned + TotalPaid - TotalOwed - WinningsPaidOut
  // Positive = manager owes player (unpaid winnings)
  // Negative = player owes manager (unpaid squares)
  const boardNetBalance = board.totalEarned + board.totalPaid - board.totalOwed - board.winningsPaidOut;
  const boardNetBalanceIsZero = Math.abs(boardNetBalance) < epsilon;

  // Board is settled if:
  // 1. Traditional check: paid up AND (no winnings OR winnings paid out), OR
  // 2. Net balance is zero (everything balances out)
  const isSettled = (isPaid && (!hasWinnings || board.winningsFullyPaid)) || boardNetBalanceIsZero;

  return (
    <Accordion
      sx={{
        mb: 1,
        bgcolor: isSettled ? '#e8f5e9' : '#fff',
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', flexWrap: 'wrap' }}>
          <Typography fontWeight="bold" sx={{ minWidth: 200 }}>
            {board.board.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {board.board.homeTeam} vs {board.board.awayTeam}
          </Typography>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 2, alignItems: 'center' }}>
            <Typography variant="body2">
              Balance: <strong style={{ color: boardNetBalanceIsZero ? '#2e7d32' : (boardNetBalance >= 0 ? '#2e7d32' : '#d32f2f') }}>
                {boardNetBalanceIsZero ? '$0.00' : (boardNetBalance >= 0 ? '+' : '-') + '$' + Math.abs(boardNetBalance).toFixed(2)}
              </strong>
            </Typography>
            {isSettled && <SettledIcon sx={{ color: 'success.main' }} />}
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {/* Financial Summary */}
        <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Squares</Typography>
            <Typography>{board.claimedSquares}/{board.approvedSquares}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Owed</Typography>
            <Typography>${board.totalOwed.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Paid</Typography>
            <Typography color="success.main">${board.totalPaid.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Winnings</Typography>
            <Typography color="info.main">${board.totalEarned.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Winnings Paid Out</Typography>
            <Typography>${board.winningsPaidOut.toFixed(2)}</Typography>
          </Box>
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {!isPaid && (
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => { e.stopPropagation(); onRecordPayment(); }}
              disabled={saving}
            >
              Record Payment
            </Button>
          )}
          {hasWinnings && (
            <Button
              size="small"
              variant="outlined"
              color={board.winningsFullyPaid ? 'success' : 'warning'}
              onClick={(e) => { e.stopPropagation(); onMarkWinningsPaid(); }}
              disabled={saving}
            >
              {board.winningsFullyPaid ? 'Winnings Paid' : 'Mark Winnings Paid'}
            </Button>
          )}
          <Button
            size="small"
            variant="text"
            component={RouterLink}
            to={`/boards/${board.board.slug || board.board.id}`}
          >
            View Board
          </Button>
        </Box>

        {/* Ledger Entries */}
        {board.ledgerEntries.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Transaction History
            </Typography>
            <Box sx={{ bgcolor: 'grey.100', borderRadius: 1, p: 1 }}>
              {board.ledgerEntries.map((entry: LedgerEntry) => (
                <Box
                  key={entry.id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    py: 0.5,
                    borderBottom: '1px solid #e0e0e0',
                    '&:last-child': { borderBottom: 'none' },
                  }}
                >
                  <Box>
                    <Typography variant="body2">
                      {formatEntryType(entry.entryType)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatAccountingDate(entry.createdAt)} - {entry.description}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color={entry.amount >= 0 ? 'success.main' : 'error.main'}
                  >
                    {entry.amount >= 0 ? '+' : ''}${entry.amount.toFixed(2)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default AccountingDetailPage;
